import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { usersApi } from '../../services/api'
import { ADMIN_FORM_PERMISSION_GROUP, ADMIN_PERMISSION_COLUMNS, ADMIN_RECRUITMENT_PERMISSION_GROUP, FORM_MODULE_KEYS, RECRUITMENT_MODULE_KEYS } from '../../constants/adminModules'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'

const EMPTY_USER = {
  name: '',
  username: '',
  password: '',
  is_admin: false,
  is_active: true,
  permissions: [],
}

function getColumnModules(column) {
  return column.modules
}

function buildFullPermissions(user, allModules) {
  return allModules.map((module) => {
    const existing = user.permissions?.find((permission) => permission.module === module)
    return {
      module,
      can_view: Boolean(existing?.can_view),
      can_create: Boolean(existing?.can_create),
      can_edit: Boolean(existing?.can_edit),
      can_delete: Boolean(existing?.can_delete),
      can_export: Boolean(existing?.can_export),
      can_publish: Boolean(existing?.can_publish),
      can_approve: Boolean(existing?.can_approve),
    }
  })
}

function hasColumnAccess(user, column) {
  if (user.is_admin) return true
  return getColumnModules(column).some((module) =>
    user.permissions?.some((permission) => permission.module === module && permission.can_view),
  )
}

function toggleColumnAccess(permissions, column, checked) {
  const modules = getColumnModules(column)
  return permissions.map((permission) =>
    modules.includes(permission.module) ? { ...permission, can_view: checked } : permission,
  )
}

function hasFormGroupAccess(user) {
  if (user.is_admin) return true
  return FORM_MODULE_KEYS.some((module) =>
    user.permissions?.some((permission) => permission.module === module && permission.can_view),
  )
}

function formGroupCheckedState(user) {
  if (user.is_admin) return { checked: true, indeterminate: false }

  const enabled = FORM_MODULE_KEYS.filter((module) =>
    user.permissions?.some((permission) => permission.module === module && permission.can_view),
  )

  return {
    checked: enabled.length === FORM_MODULE_KEYS.length,
    indeterminate: enabled.length > 0 && enabled.length < FORM_MODULE_KEYS.length,
  }
}

function toggleFormGroupAccess(permissions, checked) {
  return permissions.map((permission) =>
    FORM_MODULE_KEYS.includes(permission.module) ? { ...permission, can_view: checked } : permission,
  )
}

function hasRecruitmentGroupAccess(user) {
  if (user.is_admin) return true
  return RECRUITMENT_MODULE_KEYS.some((module) =>
    user.permissions?.some((permission) => permission.module === module && permission.can_view),
  )
}

function recruitmentGroupCheckedState(user) {
  if (user.is_admin) return { checked: true, indeterminate: false }

  const enabled = RECRUITMENT_MODULE_KEYS.filter((module) =>
    user.permissions?.some((permission) => permission.module === module && permission.can_view),
  )

  return {
    checked: enabled.length === RECRUITMENT_MODULE_KEYS.length,
    indeterminate: enabled.length > 0 && enabled.length < RECRUITMENT_MODULE_KEYS.length,
  }
}

function toggleRecruitmentGroupAccess(permissions, checked) {
  return permissions.map((permission) =>
    RECRUITMENT_MODULE_KEYS.includes(permission.module) ? { ...permission, can_view: checked } : permission,
  )
}

export default function AdminUsers() {
  const { t } = useTranslation()
  const [users, setUsers] = useState([])
  const [allModules, setAllModules] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const load = () => {
    setLoading(true)
    Promise.all([usersApi.getAll(), usersApi.getModules()])
      .then(([usersRes, modulesRes]) => {
        setUsers(usersRes.data.data || usersRes.data || [])
        setAllModules(modulesRes.data.modules || [])
      })
      .catch(() => {
        setUsers([])
        setAllModules([])
      })
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const openCreate = () => {
    setSaveError('')
    setForm({
      ...EMPTY_USER,
      permissions: buildFullPermissions({ permissions: [] }, allModules),
    })
  }

  const openEdit = (user) => {
    setSaveError('')
    setForm({
      ...user,
      password: '',
      permissions: buildFullPermissions(user, allModules),
    })
  }

  const toggleFormPermission = (column) => {
    setForm((prev) => ({
      ...prev,
      permissions: toggleColumnAccess(
        prev.permissions,
        column,
        !hasColumnAccess(prev, column),
      ),
    }))
  }

  const toggleFormGroupPermission = () => {
    setForm((prev) => ({
      ...prev,
      permissions: toggleFormGroupAccess(prev.permissions, !hasFormGroupAccess(prev)),
    }))
  }

  const toggleRecruitmentGroupPermission = () => {
    setForm((prev) => ({
      ...prev,
      permissions: toggleRecruitmentGroupAccess(prev.permissions, !hasRecruitmentGroupAccess(prev)),
    }))
  }

  const handleSaveUser = async (event) => {
    event.preventDefault()
    setSaving(true)
    setSaveError('')
    try {
      const formEl = event.currentTarget
      const passwordValue = (
        formEl?.elements?.namedItem('password')?.value ??
        form.password ??
        ''
      ).trim()

      if (!form.id && passwordValue.length < 8) {
        setSaveError(t('admin.users.passwordHint'))
        return
      }

      const payload = {
        name: form.name.trim(),
        username: form.username.trim(),
        is_admin: Boolean(form.is_admin),
        is_active: Boolean(form.is_active),
        permissions: form.is_admin ? [] : form.permissions,
      }
      if (passwordValue) payload.password = passwordValue
      if (!form.id && !payload.password) {
        setSaveError(t('admin.users.passwordHint'))
        return
      }

      if (form.id) await usersApi.update(form.id, payload)
      else await usersApi.create(payload)
      setForm(null)
      load()
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        Object.values(err?.response?.data?.errors || {})?.flat?.()?.[0] ||
        t('admin.users.saveError')
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.users.confirmDelete'))) return
    await usersApi.delete(id)
    if (form?.id === id) setForm(null)
    load()
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary">{t('admin.users.title')}</h1>
          <p className="mt-2 text-text/60">{t('admin.users.subtitle')}</p>
        </div>
        <Button variant="accent" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          {t('admin.users.add')}
        </Button>
      </div>

      {form && (
        <Card className="mb-8">
          <form onSubmit={handleSaveUser} className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <AdminFormField label={t('admin.users.name')} htmlFor="user-name">
                <input
                  id="user-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={adminInputClass}
                  required
                />
              </AdminFormField>
              <AdminFormField label={t('admin.users.username')} htmlFor="user-username">
                <input
                  id="user-username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className={adminInputClass}
                  autoComplete="off"
                  required
                />
              </AdminFormField>
              <AdminFormField
                label={form.id ? t('admin.users.newPassword') : t('admin.users.password')}
                htmlFor="user-password"
                className="md:col-span-2"
              >
                <input
                  id="user-password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={adminInputClass}
                  autoComplete="new-password"
                  required={!form.id}
                  minLength={form.id ? undefined : 8}
                />
                <p className="mt-1 text-xs text-text/50">{t('admin.users.passwordHint')}</p>
              </AdminFormField>
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(form.is_admin)}
                  onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                />
                {t('admin.users.fullAdmin')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(form.is_active)}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                {t('admin.users.active')}
              </label>
            </div>

            {form.is_admin ? (
              <p className="text-sm text-text/60">{t('admin.users.fullAdminHint')}</p>
            ) : (
              <div>
                <p className="mb-3 text-sm font-medium text-primary">{t('admin.users.matrixHint')}</p>
                <div className="grid gap-2 rounded-xl border border-border p-4 sm:grid-cols-2">
                  {ADMIN_PERMISSION_COLUMNS.map((column) => {
                    const allowed = hasColumnAccess(form, column)
                    return (
                      <label
                        key={column.labelKey}
                        className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-background"
                      >
                        <input
                          type="checkbox"
                          checked={allowed}
                          onChange={() => toggleFormPermission(column)}
                          className="h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-text/80">{t(column.labelKey)}</span>
                      </label>
                    )
                  })}

                  <div className="sm:col-span-2">
                    <div className="rounded-lg border border-border/80 bg-background/60 p-3">
                      {(() => {
                        const { checked, indeterminate } = formGroupCheckedState(form)
                        return (
                          <label className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-white">
                            <input
                              type="checkbox"
                              checked={checked}
                              ref={(input) => {
                                if (input) input.indeterminate = indeterminate
                              }}
                              onChange={toggleFormGroupPermission}
                              className="h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
                            />
                            <span className="text-sm font-medium text-text/90">
                              {t(ADMIN_FORM_PERMISSION_GROUP.labelKey)}
                            </span>
                          </label>
                        )
                      })()}
                      <div className="ms-7 mt-1 space-y-1 border-s-2 border-border/60 ps-4">
                        {ADMIN_FORM_PERMISSION_GROUP.children.map((column) => {
                          const allowed = hasColumnAccess(form, column)
                          return (
                            <label
                              key={column.labelKey}
                              className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-white"
                            >
                              <input
                                type="checkbox"
                                checked={allowed}
                                onChange={() => toggleFormPermission(column)}
                                className="h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
                              />
                              <span className="text-sm text-text/75">{t(column.labelKey)}</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <div className="rounded-lg border border-border/80 bg-background/60 p-3">
                      {(() => {
                        const { checked, indeterminate } = recruitmentGroupCheckedState(form)
                        return (
                          <label className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-white">
                            <input
                              type="checkbox"
                              checked={checked}
                              ref={(input) => {
                                if (input) input.indeterminate = indeterminate
                              }}
                              onChange={toggleRecruitmentGroupPermission}
                              className="h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
                            />
                            <span className="text-sm font-medium text-text/90">
                              {t(ADMIN_RECRUITMENT_PERMISSION_GROUP.labelKey)}
                            </span>
                          </label>
                        )
                      })()}
                      <div className="ms-7 mt-1 space-y-1 border-s-2 border-border/60 ps-4">
                        {ADMIN_RECRUITMENT_PERMISSION_GROUP.children.map((column) => {
                          const allowed = hasColumnAccess(form, column)
                          return (
                            <label
                              key={column.labelKey}
                              className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-white"
                            >
                              <input
                                type="checkbox"
                                checked={allowed}
                                onChange={() => toggleFormPermission(column)}
                                className="h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
                              />
                              <span className="text-sm text-text/75">{t(column.labelKey)}</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {saveError && <p className="text-sm text-red-600">{saveError}</p>}

            <div className="flex gap-2">
              <Button type="submit" variant="primary" disabled={saving}>
                {t('buttons.save')}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setForm(null)}>
                {t('buttons.cancel')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-background">
                <th className="px-4 py-3 text-start font-semibold text-primary">
                  {t('admin.users.name')}
                </th>
                <th className="px-4 py-3 text-start font-semibold text-primary">
                  {t('admin.users.username')}
                </th>
                <th className="px-4 py-3 text-end font-semibold text-text/70">
                  {t('admin.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className={`border-b border-border/60 hover:bg-background/50 ${
                    form?.id === user.id ? 'bg-primary/5' : ''
                  }`}
                >
                  <td className="px-4 py-3 font-medium text-primary">{user.name}</td>
                  <td className="px-4 py-3 text-text/70">{user.username}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(user)}
                        className="rounded-lg p-2 text-primary hover:bg-primary/5"
                        title={t('buttons.edit')}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(user.id)}
                        className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                        title={t('buttons.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
